/**
 * i18n bootstrap — EN + UK are first-class (backend ADR-003; FE-ADR-004).
 * Every user-facing string in the app goes through `t()`; hardcoded copy in
 * components is a review blocker. The language preference persists under
 * `osvitly.lang` (localStorage) via the browser language detector.
 */
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import enAnnouncements from './en/announcements.json';
import enApiKeys from './en/apiKeys.json';
import enAssessments from './en/assessments.json';
import enAttendance from './en/attendance.json';
import enAuth from './en/auth.json';
import enBilling from './en/billing.json';
import enCalendar from './en/calendar.json';
import enChat from './en/chat.json';
import enChildren from './en/children.json';
import enClasses from './en/classes.json';
import enCommon from './en/common.json';
import enCurriculum from './en/curriculum.json';
import enGradebook from './en/gradebook.json';
import enDashboard from './en/dashboard.json';
import enHomework from './en/homework.json';
import enInvitations from './en/invitations.json';
import enLibrary from './en/library.json';
import enMembers from './en/members.json';
import enModeration from './en/moderation.json';
import enNav from './en/nav.json';
import enNotifications from './en/notifications.json';
import enOnboarding from './en/onboarding.json';
import enProjects from './en/projects.json';
import enProgress from './en/progress.json';
import enSettings from './en/settings.json';
import ukAnnouncements from './uk/announcements.json';
import ukApiKeys from './uk/apiKeys.json';
import ukAssessments from './uk/assessments.json';
import ukAttendance from './uk/attendance.json';
import ukAuth from './uk/auth.json';
import ukBilling from './uk/billing.json';
import ukCalendar from './uk/calendar.json';
import ukChat from './uk/chat.json';
import ukChildren from './uk/children.json';
import ukClasses from './uk/classes.json';
import ukCommon from './uk/common.json';
import ukCurriculum from './uk/curriculum.json';
import ukGradebook from './uk/gradebook.json';
import ukDashboard from './uk/dashboard.json';
import ukHomework from './uk/homework.json';
import ukInvitations from './uk/invitations.json';
import ukLibrary from './uk/library.json';
import ukMembers from './uk/members.json';
import ukModeration from './uk/moderation.json';
import ukNav from './uk/nav.json';
import ukNotifications from './uk/notifications.json';
import ukOnboarding from './uk/onboarding.json';
import ukProjects from './uk/projects.json';
import ukProgress from './uk/progress.json';
import ukSettings from './uk/settings.json';

export const LANGUAGE_KEY = 'osvitly.lang';

export const SUPPORTED_LANGUAGES = ['en', 'uk'] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    nav: enNav,
    notifications: enNotifications,
    dashboard: enDashboard,
    onboarding: enOnboarding,
    settings: enSettings,
    classes: enClasses,
    invitations: enInvitations,
    members: enMembers,
    curriculum: enCurriculum,
    calendar: enCalendar,
    assessments: enAssessments,
    homework: enHomework,
    children: enChildren,
    gradebook: enGradebook,
    attendance: enAttendance,
    progress: enProgress,
    projects: enProjects,
    library: enLibrary,
    chat: enChat,
    announcements: enAnnouncements,
    moderation: enModeration,
    billing: enBilling,
    apiKeys: enApiKeys,
  },
  uk: {
    common: ukCommon,
    auth: ukAuth,
    nav: ukNav,
    notifications: ukNotifications,
    dashboard: ukDashboard,
    onboarding: ukOnboarding,
    settings: ukSettings,
    classes: ukClasses,
    invitations: ukInvitations,
    members: ukMembers,
    curriculum: ukCurriculum,
    calendar: ukCalendar,
    assessments: ukAssessments,
    homework: ukHomework,
    children: ukChildren,
    gradebook: ukGradebook,
    attendance: ukAttendance,
    progress: ukProgress,
    projects: ukProjects,
    library: ukLibrary,
    chat: ukChat,
    announcements: ukAnnouncements,
    moderation: ukModeration,
    billing: ukBilling,
    apiKeys: ukApiKeys,
  },
} as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    defaultNS: 'common',
    ns: [
      'common',
      'auth',
      'nav',
      'notifications',
      'dashboard',
      'onboarding',
      'settings',
      'classes',
      'invitations',
      'members',
      'curriculum',
      'calendar',
      'assessments',
      'homework',
      'children',
      'gradebook',
      'attendance',
      'progress',
      'projects',
      'library',
      'chat',
      'announcements',
      'moderation',
      'billing',
      'apiKeys',
    ],
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_KEY,
      caches: ['localStorage'],
    },
  });

export default i18n;
